import { NgClass, NgStyle } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';

import { filter, takeWhile } from 'rxjs/operators';

import { FieldPointInfoWindowComponent } from '../../../components/maps/info-windows/field-point/field-point-info-window';
import { KHThemeService, SitesService } from '../../../services';
import { MapFieldPointLogicService, SiteMapLogicService } from '../../../services/logic-services';
import { ColorsUtilityService } from '../../../services/utility-services';
import { HTMLRendererUtilityService } from '../../../services/utility-services/html-renderer-utility.service';
import { FieldPointIconsSize } from '../../enums';
import { IconNameUtils } from '../../utils';
import { PointCategory } from './../../../models/point-category';

@Component({
    selector: 'kh-field-point-icon',
    templateUrl: './field-point-icon.component.html',
    styleUrls: ['./field-point-icon.component.scss'],
    imports: [
        NgClass,
        NgStyle
    ]
})
export class FieldPointIconComponent implements OnInit, OnDestroy {
    @ViewChild('infoWindowContainer', { read: ViewContainerRef, static: true }) infoWindowContainer!: ViewContainerRef;
    @Input() identifier?: string;
    @Input() pointCategory: PointCategory | undefined;
    @Input() isHighlighted = false;
    @Input() size: FieldPointIconsSize = FieldPointIconsSize.Medium;

    private _isAlive = true;

    fieldPointIconsSize = FieldPointIconsSize;
    infoWindowContent: HTMLElement;

    get pinnedInfoWindow(): google.maps.InfoWindow {
        return this._siteMapLogicService.model.pinnedInfoWindow;
    }

    get infoWindow(): google.maps.InfoWindow {
        return this._siteMapLogicService.model.infoWindow;
    }

    get isPointCoordinatesBeingEdited(): boolean {
        return this._fieldPointsLogicService.model.isEditingFieldPointCoordinates;
    }

    get fieldPointData(): any {
        return this._fieldPointsLogicService.model.fieldPointData;
    }

    get iconName(): string {
        return IconNameUtils.resolve(this.pointCategory?.matIcon);
    }

    constructor(
        private _colorsUtilityService: ColorsUtilityService,
        private _htmlRendererUtilityService: HTMLRendererUtilityService,
        private _sitesService: SitesService,
        private _siteMapLogicService: SiteMapLogicService,
        private _fieldPointsLogicService: MapFieldPointLogicService,
        private _themeService: KHThemeService
    ) { }

    ngOnInit(): void {
        this._fieldPointsLogicService.fieldPointPreviewedChanged$
            .pipe(
                takeWhile(() => this._isAlive),
                filter(() => this.isFieldPointPin())
            ).subscribe((rowIdentifier: string) => {
                if (rowIdentifier !== this.identifier) {
                    this.isHighlighted = false;
                }

                else {
                    this.isHighlighted = true;
                    if (this.pinnedInfoWindow.getContent() !== this.infoWindowContent) {
                        this.setInfoWindowAndPreviewMarker(false);
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this._isAlive = false;
        if (this.pinnedInfoWindow.getContent() === this.infoWindowContent) {
            this.pinnedInfoWindow.setContent(null);
            this.closePinnedInfoWindow();
        }
        this.infoWindowContainer.clear();
    }

    getColorStyle(): { [key: string]: string; } {
        if (!this.pointCategory) {
            return {};
        }

        if (this.isHighlighted) {
            return {
                'background-color': this._colorsUtilityService.getHexOfMapElementColor(this.pointCategory.fillColor),
                'border': `solid 2px ${this._themeService.calculateTextColorForBgColor(this._colorsUtilityService.hexToColorObject(this._colorsUtilityService.getHexOfMapElementColor(this.pointCategory.fillColor))).toString('hex')}`
            };
        }

        return {
            'background-color': this._colorsUtilityService.getHexOfMapElementColor(this.pointCategory.fillColor),
        };
    }

    getFontColor(): { [key: string]: string; } {
        if (!this.pointCategory) {
            return {};
        }

        return { 'color': this._themeService.calculateTextColorForBgColor(this._colorsUtilityService.hexToColorObject(this._colorsUtilityService.getHexOfMapElementColor(this.pointCategory.fillColor))).toString('hex') };
    }

    isFieldPointPin(): boolean {
        return this.identifier !== undefined;
    }

    hover(): void {
        if (!this.isFieldPointPin() || this.isPointCoordinatesBeingEdited) {
            return;
        }

        const markerIndex = this._fieldPointsLogicService.model.categoryMarkerComponentRefs.findIndex(ref => ref.instance === this);
        const marker = this._fieldPointsLogicService.model.categoryMarkers[markerIndex];

        if (this._fieldPointsLogicService.model.categoryMarkerBeingPreviewed === marker) {
            return;
        }

        this.clearHideInfoWindowTimeout();
        this.createInfoWindowContent();

        if (marker) {
            this.infoWindow.setContent(this.infoWindowContent);
            this.infoWindow.setPosition(marker.position);
            this.infoWindow.open({ anchor: marker, map: marker.map });
        }
    }

    hoverOut(): void {
        if (!this.isFieldPointPin()) {
            return;
        }

        this.setHideInfoWindowTimeout();
    }

    click(): void {
        if (!this.isFieldPointPin() || this.fieldPointData) {
            return;
        }

        this._fieldPointsLogicService.clearPreviewFieldPoint();

        this.setInfoWindowAndPreviewMarker(true);
    }

    setInfoWindowAndPreviewMarker(setMarkerBeingPreviewed: boolean): void {
        const markerIndex = this._fieldPointsLogicService.model.categoryMarkerComponentRefs.findIndex(ref => ref.instance === this);
        const marker = this._fieldPointsLogicService.model.categoryMarkers[markerIndex];

        this.clearHideInfoWindowTimeout();
        this.closeInfoWindow();
        this.createInfoWindowContent();
        if (marker) {
            this.pinnedInfoWindow.setContent(this.infoWindowContent);
            this.pinnedInfoWindow.setPosition(marker.position);
            this.pinnedInfoWindow.open({ anchor: marker, map: marker.map });
        }
        this.isHighlighted = true;

        this._siteMapLogicService.model.map.panTo(marker.position);
        if (setMarkerBeingPreviewed) {
            this._fieldPointsLogicService.setMarkerBeingPreviewed(this.identifier, marker);
        }
    }

    clearHideInfoWindowTimeout(): void {
        this._fieldPointsLogicService.clearHideInfoWindowTimeout();
    }

    setHideInfoWindowTimeout(): void {
        this._fieldPointsLogicService.setHideInfoWindowTimeout(this.infoWindow);
    }

    closeInfoWindow(): void {
        this._siteMapLogicService.closeInfoWindow();
    }

    closePinnedInfoWindow(): void {
        this._siteMapLogicService.closePinnedInfoWindow();
    }

    private createInfoWindowContent(): void {
        this.infoWindowContainer.clear();
        const fieldPointRow = this._sitesService.model.currentSite?.data ? this._sitesService.model.currentSite?.data[this._fieldPointsLogicService.model.mapFieldPoint.id].find(fieldPointRow => fieldPointRow.id === this.identifier) : undefined;
        const columnIdentifierKey = fieldPointRow
            ? Object.keys(fieldPointRow).find(key => key !== 'id')
            : undefined;
        const columnIdentifierValue = columnIdentifierKey ? fieldPointRow[columnIdentifierKey] : undefined;
        this.infoWindowContent = this._htmlRendererUtilityService.createComponentElement(
            FieldPointInfoWindowComponent,
            this.infoWindowContainer,
            {
                pointCategory: this.pointCategory,
                columnIdentifierValue: columnIdentifierValue,
                identifier: this.identifier
            }
        ).element;
    }
}
